/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { postGuestlist } from "../../api/orderApi";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import { buildStaffClubEventQuery } from "../../helpers/eventClubScope";
import {
  buildEventTierOptions,
  findTicketTierById,
  isValidGuestlistPhone,
  parseIndianMobilePhoneInput,
  pickDefaultTicketTierId,
} from "../../helpers/guestlistTickets";
import useAuth from "../../hooks/useAuth";
import Constant from "../../util/constant";
import { currentEventListResponce } from "../../store/features/event/eventSlice";
import { clubListResponce } from "../../store/features/club/clubSlice";
import { showToast } from "../../store/features/toast/toastSlice";

const DEFAULT_CURRENCY =
  process.env.REACT_APP_DEFAULT_CURRENCY || "INR";

const GUESTLIST_QTY_MIN = 1;
const GUESTLIST_QTY_MAX = 50;

function clampGuestlistQty(value) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return GUESTLIST_QTY_MIN;
  return Math.min(
    GUESTLIST_QTY_MAX,
    Math.max(GUESTLIST_QTY_MIN, parsed)
  );
}

function eventClubId(ev) {
  if (!ev || typeof ev !== "object") return null;
  const c = ev.clubid ?? ev.clubId;
  if (c != null && c !== "") return c;
  if (ev.club && typeof ev.club === "object") {
    return ev.club._id ?? ev.club.id ?? null;
  }
  return ev.club ?? null;
}

/**
 * POST /order/guestlist — complimentary guests against real event tiers. Manager & admin only.
 */
const GuestlistContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { managedClubId, isAdmin } = useAuth();
  const events = useSelector((s) => s?.event?.currentEventListResponce ?? []);
  const clubList = useSelector((s) => s?.club?.clubList ?? []);

  const [adminClubId, setAdminClubId] = useState("");
  const effectiveClubId = useMemo(() => {
    if (isAdmin && adminClubId) return String(adminClubId).trim();
    if (managedClubId) return String(managedClubId).trim();
    return null;
  }, [isAdmin, adminClubId, managedClubId]);

  const [eventId, setEventId] = useState("");
  const [ticketTypeId, setTicketTypeId] = useState("");
  const [noofticket, setNoofticket] = useState(String(GUESTLIST_QTY_MIN));
  const [buyername, setBuyername] = useState("");
  const [buyerphone, setBuyerphone] = useState("");
  const [buyeremail, setBuyeremail] = useState("");
  const [busy, setBusy] = useState(false);
  const [failOpen, setFailOpen] = useState(false);
  const [failMessage, setFailMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [lastGuestSummary, setLastGuestSummary] = useState(null);

  useEffect(() => {
    if (isAdmin) dispatch(clubListResponce());
  }, [isAdmin, dispatch]);

  useEffect(() => {
    if (!effectiveClubId) return;
    const q = buildStaffClubEventQuery(effectiveClubId);
    if (!q) return;
    dispatch(currentEventListResponce(q));
  }, [effectiveClubId, dispatch, location.pathname]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !effectiveClubId) return;
      const q = buildStaffClubEventQuery(effectiveClubId);
      if (!q) return;
      dispatch(currentEventListResponce(q));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [effectiveClubId, dispatch]);

  const filteredEvents = useMemo(() => {
    return (events || []).filter((ev) => {
      if (ev?.eventstatus === Constant.GENERALKEYS.CANCEL) return false;
      const cid = eventClubId(ev);
      if (cid == null || cid === "") return false;
      if (String(cid) !== String(effectiveClubId)) return false;
      return true;
    });
  }, [events, effectiveClubId]);

  const event = useMemo(
    () => (events || []).find((e) => String(e._id) === String(eventId)),
    [events, eventId]
  );

  const tierOptions = useMemo(
    () =>
      buildEventTierOptions(event?.tickets, {
        soldOutLabel: t("GUESTLIST_SOLD_OUT"),
      }),
    [event?.tickets, t]
  );

  const hasTicketTiers = tierOptions.length > 0;

  useEffect(() => {
    if (!eventId) {
      setTicketTypeId("");
      setNoofticket(String(GUESTLIST_QTY_MIN));
      return;
    }
    setNoofticket(String(GUESTLIST_QTY_MIN));
    const defaultId = pickDefaultTicketTierId(event?.tickets);
    setTicketTypeId(defaultId);
  }, [eventId, event?.tickets]);

  const selectedTier = useMemo(
    () => findTicketTierById(event?.tickets, ticketTypeId),
    [event?.tickets, ticketTypeId]
  );

  const qty = clampGuestlistQty(noofticket);

  const resetGuestFields = () => {
    setBuyername("");
    setBuyerphone("");
    setBuyeremail("");
    setNoofticket(String(GUESTLIST_QTY_MIN));
  };

  const buildPayload = () => {
    if (!effectiveClubId) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_WEBSITE_NO_CLUB"),
          type: "error",
        })
      );
      return null;
    }
    if (!eventId) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_EVENT_PLACEHOLDER"),
          type: "error",
        })
      );
      return null;
    }
    const evClub = eventClubId(event);
    if (!evClub || String(evClub) !== String(effectiveClubId)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_CLUB_MISMATCH"),
          type: "error",
        })
      );
      return null;
    }

    const nameTrim = (buyername ?? "").trim();
    const phoneTrim = (buyerphone ?? "").trim();
    const emailTrim = (buyeremail ?? "").trim();
    if (!nameTrim || !phoneTrim || !emailTrim) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_BUYER_REQUIRED"),
          type: "error",
        })
      );
      return null;
    }
    if (!Constant.REGEX.EMAIL.test(emailTrim)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_BUYER_EMAIL_INVALID"),
          type: "error",
        })
      );
      return null;
    }
    if (phoneTrim.length < 5) {
      dispatch(
        showToast({
          show: false,
          message: t("GUESTLIST_PHONE_MIN"),
          type: "error",
        })
      );
      return null;
    }
    if (!isValidGuestlistPhone(phoneTrim)) {
      dispatch(
        showToast({
          show: false,
          message: t("GUESTLIST_PHONE_INVALID"),
          type: "error",
        })
      );
      return null;
    }

    if (!ticketTypeId) {
      dispatch(
        showToast({
          show: false,
          message: t("GUESTLIST_TICKETTYPE_REQUIRED"),
          type: "error",
        })
      );
      return null;
    }
    const hit = tierOptions.find(
      (o) => String(o.ticketTypeId) === String(ticketTypeId)
    );
    if (!hit || hit.soldOut) {
      dispatch(
        showToast({
          show: false,
          message: t("GUESTLIST_CATEGORY_UNAVAILABLE"),
          type: "error",
        })
      );
      return null;
    }
    if (hit.remaining != null && qty > hit.remaining) {
      dispatch(
        showToast({
          show: false,
          message: t("GUESTLIST_INVENTORY_EXCEEDED", { count: hit.remaining }),
          type: "error",
        })
      );
      return null;
    }

    const body = {
      eventid: eventId,
      tickettypeid: ticketTypeId,
      buyername: nameTrim,
      buyeremail: emailTrim,
      buyerphone: parseIndianMobilePhoneInput(phoneTrim),
      noofticket: qty,
      currency: DEFAULT_CURRENCY,
    };
    return body;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const body = buildPayload();
    if (!body || busy) return;
    setBusy(true);
    postGuestlist(body)
      .then(() => {
        setLastGuestSummary({
          name: body.buyername,
          email: body.buyeremail,
          phone: body.buyerphone,
          ticketCount: body.noofticket,
          category: selectedTier?.tickettype ?? "",
        });
        dispatch(
          showToast({
            show: false,
            message: t("GUESTLIST_SUCCESS_TOAST"),
            type: "success",
          })
        );
        setSuccessOpen(true);
        resetGuestFields();
      })
      .catch((err) => {
        const message =
          typeof err === "string"
            ? err
            : err?.message || t("GUESTLIST_ERROR");
        setFailMessage(message);
        setFailOpen(true);
        dispatch(
          showToast({
            show: false,
            message,
            type: "error",
          })
        );
      })
      .finally(() => setBusy(false));
  };

  const closeSuccessModal = () => {
    setSuccessOpen(false);
    setLastGuestSummary(null);
  };

  const handleAddAnother = () => {
    setSuccessOpen(false);
    setLastGuestSummary(null);
    resetGuestFields();
  };

  const pageTitle = [{ title: t("GUESTLIST_TITLE"), path: "#" }];

  return (
    <div className="pageContent categoryManagement club-staff-page-shell club-offline-sale-page club-guestlist-page">
      <Helmet>
        <title>{t("GUESTLIST_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("GUESTLIST_TITLE")}
        breadcrumbItem={t("GUESTLIST_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />
      <Card className="border-0">
        <Card.Body>
          <p className="small text-muted mb-3">{t("GUESTLIST_HELP")}</p>
          {!effectiveClubId && !isAdmin ? (
            <p className="text-danger">{t("CLUB_WEBSITE_NO_CLUB")}</p>
          ) : (
            <Form onSubmit={onSubmit} className="mt-2">
              {isAdmin ? (
                <Form.Group className="mb-3">
                  <Form.Label>{t("CLUB_SALES_ADMIN_CLUB_FILTER")}</Form.Label>
                  <Form.Select
                    value={adminClubId}
                    onChange={(e) => {
                      setAdminClubId(e.target.value);
                      setEventId("");
                    }}
                    required
                  >
                    <option value="">{t("CLUB_SALES_ALL_PARTNERS")}</option>
                    {(clubList || []).map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name || c.slug || c._id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              ) : null}

              <Form.Group className="mb-3">
                <Form.Label>{t("OFFLINE_SALE_EVENT")}</Form.Label>
                <Form.Select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  required
                  disabled={!effectiveClubId}
                >
                  <option value="">{t("OFFLINE_SALE_EVENT_PLACEHOLDER")}</option>
                  {filteredEvents.map((ev) => (
                    <option key={ev._id} value={ev._id}>
                      {ev.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {eventId ? (
                hasTicketTiers ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t("GUESTLIST_CATEGORY")}{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={ticketTypeId}
                      onChange={(e) => setTicketTypeId(e.target.value)}
                      required
                    >
                      <option value="">
                        {t("GUESTLIST_CATEGORY_PLACEHOLDER")}
                      </option>
                      {tierOptions.map((opt) => (
                        <option
                          key={opt.ticketTypeId}
                          value={opt.ticketTypeId}
                          disabled={opt.soldOut}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                ) : (
                  <p className="small text-warning mb-3">
                    {t("GUESTLIST_NO_CATEGORY")}
                  </p>
                )
              ) : null}

              <Row className="g-3 mb-3">
                <Col xs={12} sm={4} md={3}>
                  <Form.Group>
                    <Form.Label>
                      {t("GUESTLIST_QTY")}{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={GUESTLIST_QTY_MIN}
                      max={GUESTLIST_QTY_MAX}
                      step={1}
                      value={noofticket}
                      onChange={(e) => setNoofticket(e.target.value)}
                      onBlur={() =>
                        setNoofticket(String(clampGuestlistQty(noofticket)))
                      }
                      disabled={!eventId}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <p className="small text-muted fw-semibold mb-2">
                {t("GUESTLIST_GUEST_SECTION")}
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("GUESTLIST_GUEST_NAME")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={buyername}
                  onChange={(e) => setBuyername(e.target.value)}
                  autoComplete="name"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("GUESTLIST_GUEST_EMAIL")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={buyeremail}
                  onChange={(e) => setBuyeremail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("GUESTLIST_GUEST_PHONE")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={buyerphone}
                  onChange={(e) => setBuyerphone(e.target.value)}
                  autoComplete="tel"
                  required
                  minLength={5}
                />
              </Form.Group>

              <div className="d-flex justify-content-center mt-2">
                <CustomButton
                  type="submit"
                  className="fillButton club-staff-btn-wide-mobile"
                  disabled={busy || !effectiveClubId || !eventId || !ticketTypeId}
                >
                  {busy ? t("LOADING") : t("GUESTLIST_SUBMIT")}
                </CustomButton>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={failOpen}
        onHide={() => setFailOpen(false)}
        centered
        backdropClassName="club-offline-modal-backdrop"
        className="club-offline-sale-fail-modal"
        contentClassName="border-0 rounded-3 shadow"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold text-uppercase fs-6 text-danger mb-0">
            {t("OFFLINE_SALE_FAIL_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <p className="mb-0 small">{failMessage}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <button
            type="button"
            className="btn btn-primary btn-sm px-4"
            onClick={() => setFailOpen(false)}
          >
            {t("OFFLINE_SALE_FAIL_OK")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={successOpen}
        onHide={closeSuccessModal}
        centered
        scrollable
        backdropClassName="club-offline-modal-backdrop"
        dialogClassName="club-offline-sale-success-dialog"
        className="club-offline-sale-result-modal club-offline-sale-result-success club-guestlist-success-modal"
        contentClassName="d-flex flex-column border-0 shadow"
      >
        <Modal.Header
          closeButton
          className="flex-shrink-0 border-0 pb-0 club-offline-sale-success-header"
        />
        <Modal.Body className="px-3 px-sm-4 text-center pt-0 pb-4 club-offline-sale-success-body">
          <h2 className="fw-bold fs-4 mb-2">{t("GUESTLIST_SUCCESS_TITLE")}</h2>
          {lastGuestSummary ? (
            <dl className="row mb-3 small text-start club-offline-confirm-summary mx-auto club-guestlist-success-summary">
              <dt className="col-5 text-muted fw-normal">
                {t("GUESTLIST_GUEST_NAME")}
              </dt>
              <dd className="col-7">{lastGuestSummary.name}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("GUESTLIST_GUEST_EMAIL")}
              </dt>
              <dd className="col-7 text-break">{lastGuestSummary.email}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("GUESTLIST_GUEST_PHONE")}
              </dt>
              <dd className="col-7">{lastGuestSummary.phone}</dd>
              {lastGuestSummary.category ? (
                <>
                  <dt className="col-5 text-muted fw-normal">
                    {t("GUESTLIST_CATEGORY")}
                  </dt>
                  <dd className="col-7">{lastGuestSummary.category}</dd>
                </>
              ) : null}
              <dt className="col-5 text-muted fw-normal">
                {t("GUESTLIST_QTY")}
              </dt>
              <dd className="col-7">{lastGuestSummary.ticketCount}</dd>
            </dl>
          ) : null}

          <button
            type="button"
            className="btn btn-link text-decoration-none fw-semibold d-block mx-auto mt-3"
            onClick={handleAddAnother}
          >
            {t("GUESTLIST_ADD_ANOTHER")}
          </button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default GuestlistContainer;
